import { useEffect, useState } from 'react';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import type { EnrichedReadyToSharePlatformPostsV2 } from '#src/app/shared/api/readyToShare';

import type {
	PostTypeDataMap,
	ReadyToSharePlatformPostType,
	UseFetchReadyToSharePostsByTypeReturn,
} from '../useFetchReadyToSharePostsByType';
import { useFetchReadyToSharePostsByType } from '../useFetchReadyToSharePostsByType';

// Post types to fetch ready-to-share posts for. Covers all 6 supported platform post types.
// Update this list if additional post types are supported in the future.
const POST_TYPES_TO_FETCH = [
	'FACEBOOK_POST',
	'FACEBOOK_STORY',
	'FACEBOOK_GROUPS_POST',
	'INSTAGRAM_POST',
	'INSTAGRAM_STORY',
	'REDDIT_GROUPS_POST',
] as const satisfies readonly ReadyToSharePlatformPostType[];

// Display priority order. The first post type with available data is shown as the "hero" post.
// Higher-reach platforms (Facebook, Instagram) are preferred over niche ones.
const POST_TYPE_PRIORITY = [
	'FACEBOOK_POST',
	'INSTAGRAM_POST',
	'REDDIT_GROUPS_POST',
	'FACEBOOK_STORY',
	'INSTAGRAM_STORY',
	'FACEBOOK_GROUPS_POST',
] as const satisfies readonly ReadyToSharePlatformPostType[];

/**
 * Custom hook that handles the effect logic for post type data changes
 */
function usePostTypeDataEffect(
	postTypeDataResult: UseFetchReadyToSharePostsByTypeReturn,
	setLoading: (loading: boolean) => void,
	setShouldShowReadyToShareExperiment: (show: boolean) => void,
	setReadyToSharePostsValue: (posts: EnrichedReadyToSharePlatformPostsV2 | undefined) => void,
) {
	const { postTypeData, hasAnyData, allComplete, allFailed, anyPending, allDone } = postTypeDataResult;

	useEffect(() => {
		/**
		 * Stop if all post types failed
		 */
		if (allFailed) {
			setLoading(false);
			setShouldShowReadyToShareExperiment(false);
			return;
		}

		/**
		 * Progressive loading: Show data as soon as available
		 * Stop showing loading UI immediately when first post type has data
		 * (Polling continues in background until all post types are done)
		 */

		// Find first post type with complete data based on priority order
		const firstAvailablePostType = POST_TYPE_PRIORITY.find((postType) => {
			const data = postTypeData[postType];
			return data?.status === 'COMPLETE' && data.posts.length > 0;
		});

		// Show posts from the first available post type
		if (firstAvailablePostType && postTypeData[firstAvailablePostType]) {
			setReadyToSharePostsValue(postTypeData[firstAvailablePostType].posts);
		}

		// Stop loading UI as soon as we have ANY data
		if (hasAnyData) {
			setLoading(false);
		}

		// If all post types are done and still no data, hide the experiment
		if (allDone && !hasAnyData) {
			setLoading(false);
			setShouldShowReadyToShareExperiment(false);
		}
	}, [
		hasAnyData,
		allComplete,
		allFailed,
		anyPending,
		allDone,
		postTypeData,
		setLoading,
		setReadyToSharePostsValue,
		setShouldShowReadyToShareExperiment,
	]);
}

export function useHandlePolling() {
	const {
		data: {
			petition: { slug },
			viewer: {
				experiments: { starterExperienceDashboardReadyToShareV2 },
			},
		},
	} = useStarterDashboardV2PageData();
	const isVariation = starterExperienceDashboardReadyToShareV2.variation === 'variant1';
	/**
	 * Initialize module visibility based on experiment variation only.
	 * The module will start polling for data immediately and hide itself if no data is found after all post types complete.
	 * This ensures the module appears for new petitions without preview links yet.
	 */
	const [shouldShowReadyToShareExperiment, setShouldShowReadyToShareExperiment] = useState<boolean>(isVariation);
	const [isLoading, setLoading] = useState<boolean>(isVariation);
	const [readyToSharePostsValue, setReadyToSharePostsValue] = useState<EnrichedReadyToSharePlatformPostsV2 | undefined>(
		undefined,
	);

	const postTypeDataResult = useFetchReadyToSharePostsByType({
		slug,
		postTypes: POST_TYPES_TO_FETCH,
		shouldExecute: shouldShowReadyToShareExperiment,
	});

	// Handle post type data changes with progressive loading
	usePostTypeDataEffect(postTypeDataResult, setLoading, setShouldShowReadyToShareExperiment, setReadyToSharePostsValue);

	return {
		isLoading,
		readyToSharePostsValue,
		shouldShowReadyToShareExperiment,
		postTypeData: postTypeDataResult.postTypeData, // Expose for progressive loading UI
	};
}

export type { PostTypeDataMap };
